import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Send, Clock, FileText, CheckCircle, Loader2 } from 'lucide-react';

const QuestionPaperView = ({ questionPaper, answers, onAnswerChange, readOnly = false, submission = null }) => {
  if (!questionPaper) return null;

  const renderQuestion = (question, sectionIdx, questionIdx) => {
    const key = `${sectionIdx}-${questionIdx}`;
    const currentAnswer = answers[key] || '';
    const qType = (question.type || '').toLowerCase();
    const submittedAnswer = submission?.answers?.[key];
    const isCorrect = submittedAnswer && question.correct_answer && 
      submittedAnswer.toString().toLowerCase() === question.correct_answer.toString().toLowerCase();

    // Detect question type
    const isMCQ = qType === 'mcq' || qType === 'multiple_choice' || qType === 'single_choice';
    const isMultiSelect = qType === 'multi_select' || qType === 'multiple_select' || qType === 'checkbox';
    const isTrueFalse = qType === 'true_false' || qType === 'truefalse' || qType === 'boolean';
    const isFillBlank = qType === 'fill_in_the_blank' || qType === 'fill_blank' || qType === 'fill_in_blanks' || qType === 'fillintheblank';
    const isShortAnswer = qType === 'short_answer' || qType === 'short';
    const isLongAnswer = qType === 'long_answer' || qType === 'long' || qType === 'essay' || qType === 'descriptive';

    return (
      <div key={key} className="p-4 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors" data-testid={`question-${key}`}>
        <div className="flex items-start gap-3 mb-3">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
            {question.number || questionIdx + 1}
          </span>
          <div className="flex-1">
            <p className="text-gray-800 font-medium leading-relaxed">{question.text}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs capitalize">{question.type || 'question'}</Badge>
              <span className="text-xs text-gray-400">{question.marks || 1} mark{(question.marks || 1) > 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>

        <div className="ml-10">
          {/* MCQ / Single Choice - Radio Buttons */}
          {(isMCQ && !isMultiSelect) && question.options && (
            <RadioGroup 
              value={readOnly ? (submittedAnswer || '') : currentAnswer} 
              onValueChange={v => !readOnly && onAnswerChange(key, v)}
              disabled={readOnly}
              className="space-y-2"
            >
              {question.options.map((opt, oIdx) => {
                const optValue = opt.charAt(0);
                const isSelected = (readOnly ? submittedAnswer : currentAnswer) === optValue;
                return (
                  <div key={oIdx} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                    isSelected 
                      ? readOnly
                        ? isCorrect ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'
                        : 'bg-blue-50 border-blue-300'
                      : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}>
                    <RadioGroupItem value={optValue} id={`${key}-opt-${oIdx}`} />
                    <Label htmlFor={`${key}-opt-${oIdx}`} className="text-sm cursor-pointer flex-1">{opt}</Label>
                  </div>
                );
              })}
            </RadioGroup>
          )}

          {/* Multi-Select - Checkboxes */}
          {isMultiSelect && question.options && (
            <div className="space-y-2">
              {question.options.map((opt, oIdx) => {
                const optValue = opt.charAt(0);
                const selectedArray = readOnly 
                  ? (Array.isArray(submittedAnswer) ? submittedAnswer : []) 
                  : (Array.isArray(currentAnswer) ? currentAnswer : []);
                const isChecked = selectedArray.includes(optValue);
                return (
                  <div key={oIdx} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                    isChecked ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}>
                    <Checkbox 
                      id={`${key}-chk-${oIdx}`}
                      checked={isChecked}
                      disabled={readOnly}
                      onCheckedChange={(checked) => {
                        if (readOnly) return;
                        const prev = Array.isArray(currentAnswer) ? [...currentAnswer] : [];
                        if (checked) {
                          onAnswerChange(key, [...prev, optValue]);
                        } else {
                          onAnswerChange(key, prev.filter(v => v !== optValue));
                        }
                      }}
                    />
                    <Label htmlFor={`${key}-chk-${oIdx}`} className="text-sm cursor-pointer flex-1">{opt}</Label>
                  </div>
                );
              })}
            </div>
          )}

          {/* True/False - Radio Buttons */}
          {isTrueFalse && (
            <RadioGroup 
              value={readOnly ? (submittedAnswer || '') : currentAnswer} 
              onValueChange={v => !readOnly && onAnswerChange(key, v)}
              disabled={readOnly}
              className="flex gap-4"
            >
              {['True', 'False'].map(val => {
                const isSelected = (readOnly ? submittedAnswer : currentAnswer) === val;
                return (
                  <div key={val} className={`flex items-center gap-2 px-6 py-3 rounded-lg border cursor-pointer transition-all ${
                    isSelected 
                      ? readOnly
                        ? isCorrect ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'
                        : 'bg-blue-50 border-blue-300'
                      : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}>
                    <RadioGroupItem value={val} id={`${key}-tf-${val}`} />
                    <Label htmlFor={`${key}-tf-${val}`} className="text-sm font-medium cursor-pointer">{val}</Label>
                  </div>
                );
              })}
            </RadioGroup>
          )}

          {/* Fill in the Blanks - Input */}
          {isFillBlank && (
            <Input
              data-testid={`fill-blank-${key}`}
              placeholder="Type your answer here..."
              value={readOnly ? (submittedAnswer || '') : currentAnswer}
              onChange={e => !readOnly && onAnswerChange(key, e.target.value)}
              disabled={readOnly}
              className="max-w-md"
            />
          )}

          {/* Short Answer - Input */}
          {isShortAnswer && (
            <Input
              data-testid={`short-answer-${key}`}
              placeholder="Write your short answer..."
              value={readOnly ? (submittedAnswer || '') : currentAnswer}
              onChange={e => !readOnly && onAnswerChange(key, e.target.value)}
              disabled={readOnly}
              className="max-w-lg"
            />
          )}

          {/* Long Answer / Essay - Textarea */}
          {isLongAnswer && (
            <Textarea
              data-testid={`long-answer-${key}`}
              placeholder="Write your detailed answer here..."
              value={readOnly ? (submittedAnswer || '') : currentAnswer}
              onChange={e => !readOnly && onAnswerChange(key, e.target.value)}
              disabled={readOnly}
              rows={5}
            />
          )}

          {/* Default fallback: if question type is unknown but has options → radio, else → textarea */}
          {!isMCQ && !isMultiSelect && !isTrueFalse && !isFillBlank && !isShortAnswer && !isLongAnswer && (
            question.options && question.options.length > 0 ? (
              <RadioGroup 
                value={readOnly ? (submittedAnswer || '') : currentAnswer} 
                onValueChange={v => !readOnly && onAnswerChange(key, v)}
                disabled={readOnly}
                className="space-y-2"
              >
                {question.options.map((opt, oIdx) => {
                  const optValue = opt.charAt(0);
                  const isSelected = (readOnly ? submittedAnswer : currentAnswer) === optValue;
                  return (
                    <div key={oIdx} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                      isSelected ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <RadioGroupItem value={optValue} id={`${key}-def-${oIdx}`} />
                      <Label htmlFor={`${key}-def-${oIdx}`} className="text-sm cursor-pointer flex-1">{opt}</Label>
                    </div>
                  );
                })}
              </RadioGroup>
            ) : (
              <Textarea
                placeholder="Write your answer here..."
                value={readOnly ? (submittedAnswer || '') : currentAnswer}
                onChange={e => !readOnly && onAnswerChange(key, e.target.value)}
                disabled={readOnly}
                rows={3}
              />
            )
          )}

          {/* Show correct answer in read-only mode */}
          {readOnly && submittedAnswer && question.correct_answer && (
            <div className={`mt-2 text-xs px-2 py-1 rounded ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {isCorrect ? 'Correct' : `Correct answer: ${question.correct_answer}`}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="question-paper-view">
      {/* Paper Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
        <h2 className="text-lg font-bold text-gray-900">{questionPaper.title || 'Question Paper'}</h2>
        <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
          {questionPaper.subject && <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {questionPaper.subject}</span>}
          {questionPaper.total_marks && <span>Total: {questionPaper.total_marks} marks</span>}
          {questionPaper.duration && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {questionPaper.duration}</span>}
        </div>
        {questionPaper.instructions?.length > 0 && (
          <div className="mt-3 p-2 bg-white/60 rounded text-xs text-gray-500">
            <span className="font-medium">Instructions: </span>
            {questionPaper.instructions.join(' | ')}
          </div>
        )}
      </div>

      {/* Sections */}
      {(questionPaper.sections || []).map((section, sIdx) => (
        <div key={sIdx} data-testid={`paper-section-${sIdx}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">{section.name}</h3>
            <Badge variant="outline" className="text-xs">{section.questions?.length || 0} questions</Badge>
          </div>
          {section.description && <p className="text-xs text-gray-500 mb-3">{section.description}</p>}
          <div className="space-y-3">
            {(section.questions || []).map((q, qIdx) => renderQuestion(q, sIdx, qIdx))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default QuestionPaperView;
